import { App } from '../App';
import * as React from 'react';
import { styled } from 'styled-components';

import { Map } from '@longline/aqua-ui/map/Map';
import { Dock, Dockable } from '@longline/aqua-ui/containers/Dock';

import { useGlobalState } from '../contexts/state/GlobalStateContext';
//import { useConfig } from '../services/config/IConfig';
import { MapControls } from './MapControls';
import { CircleBuilder, CircleEditor } from '../editors/CirleEditor';
import { PolygonBuilder, PolygonEditor } from '../editors/PolygonEditor';
import { StructureInfoBox } from '../infoboxes/StructureInfoBox';
import { ReferenceLayers } from './ReferenceLayers';
import { DataLayers } from './DataLayers';
import { StructuresLayer } from './StructuresLayer';
import { GridsLayer } from './GridsLayer';
import { MarkerLayer } from './MarkerLayer';
import { Legends } from './Legends';
import { IStructure } from '../types/IStructure';
import { RectBuilder, RectEditor } from '../editors/RectEditor';
import { AreaInfoBox } from '../infoboxes/AreaInfoBox';
import { AreasLayer } from './AreasLayer';
import { PolygonUtils } from '../util/PolygonUtils';
import { OrganixManager } from '../organix/OrganixManager';
import { BoxSelectBuilder } from '../editors/BoxSelectBuilder';
import { MultiStructureOverlay } from '../editors/MultiStructureOverlay';
import { Eyedropper } from '../editors/Eyedropper';
import { LocalMap } from './LocalMap';

interface IProps {
  /** @ignore */
  className?: string;
}

const MapViewBase = (props: IProps) => {
  //const config = useConfig();
  const globalState = useGlobalState();
  const copiedStructures = React.useRef<IStructure[]>([]);

  React.useEffect(() => {
    // TODO: REMOVE THIS!
    // Clears entire Mapbox tile cache for testing of clipping strategy.
    // mapboxgl.clearStorage();
  }, []);

  const handleKeydown = (e: React.KeyboardEvent) => {
    // Ctrl+A: Select all structures
    if(e.code == 'KeyA' && e.ctrlKey && globalState.structures.length > 0) {
      e.preventDefault();
      globalState.selectAllStructures();
      return;
    }

    // Delete key: Delete all selected structures
    if((e.code == 'Delete' || e.code == 'NumpadDecimal') && globalState.selectedStructures.length > 0 && globalState.mode === null) {
      globalState.deleteSelectedStructures();
      return;
    }

    // Ctrl+C: Copy selected structures to memory
    if(e.code == 'KeyC' && e.ctrlKey && globalState.selectedStructures.length > 0) {
      // Make a deep copy of all selected structures:
      copiedStructures.current = JSON.parse(JSON.stringify(globalState.selectedStructures));
      return;
    }

    // Ctrl+V: Paste structures from memory
    if(e.code == 'KeyV' && e.ctrlKey && copiedStructures.current.length > 0) {
      // Offset each pasted structure slightly.
      for (const structure of copiedStructures.current) {
        if(structure.type == 'circle') {
          // Move circle center by one radius right and down:
          const p = PolygonUtils.addMeters(structure.point, structure.radius, -structure.radius);
          structure.point = p;
        } else {
          // Move each point on polygon by one-half polygon bbox right and down:
          const bbox = PolygonUtils.getBbox(structure.points);
          const width = Math.abs(bbox.getEast() - bbox.getWest()) / 2
          const height = Math.abs(bbox.getNorth() - bbox.getSouth()) / 2;
          structure.points = structure.points.map(p => { return { lng: p.lng + width, lat: p.lat - height }; });
        }
      }
      // Add all copied structures (paste first one with addStructure for selection)
      if (copiedStructures.current.length === 1) {
        globalState.addStructure(JSON.parse(JSON.stringify(copiedStructures.current[0])));
      } else {
        // For multiple structures, add all and select all
        const newStructures: IStructure[] = copiedStructures.current.map((s, i) => ({
          ...JSON.parse(JSON.stringify(s)),
          name: `Structure ${globalState.structures.length + i + 1}`
        }));
        globalState.setStructures([...globalState.structures, ...newStructures]);
        globalState.setSelectedStructures(newStructures);
      }
      return;
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    // Shift+click starts box select mode for multi-structure selection
    if (e.shiftKey && globalState.mode === null && globalState.structures.length > 0) {
      e.preventDefault();
      globalState.setMode('box-select');
    }
  };

  return (
    <div
      id="map-container"
      className={props.className}
      // If mapholder is clicked:
      onClick={() => {
        // Structure code requires mouse clicks; don't cancel it.
        if(globalState.mode == 'circle-builder') return;
        if(globalState.mode == 'polygon-builder') return;
        if(globalState.mode == 'area-builder') return;
        if(globalState.mode == 'area-editor') return;
        if(globalState.mode == 'box-select') return;
        if(globalState.mode == 'eyedropper') return;
        if(globalState.mode == 'areas' && globalState.selectedArea != null) {
          globalState.setMode('area-editor')
          return;
        }
        globalState.setMode(null);
      }}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeydown}
    >
      <LocalMap
        style="/static/mapbox-style.json"
        token={App.config.mapboxKey}
        clipped={globalState.project.clipping}
        latitude={0}
        longitude={0}
        zoom={2}
        logoPosition='bottom-left'
        interactiveLayerIds={["structures"]}
        //noRotate
        preserveDrawingBuffer
        onLoad={(map) => {
          map.boxZoom.disable(); // Disable Mapbox's built-in box zoom
          globalState.setMap(map);
        }}
      >
        <div data-html2canvas-ignore>
          <MapControls />
        </div>

        <ReferenceLayers/>
        <DataLayers/>
        {/* Farms grouping is disabled at the customer's request; only Grids are relevant.
            The machinery is intact in FarmsLayer/FarmUtils if it comes back. */}
        {globalState.showStructures && <GridsLayer/>}
        {globalState.showStructures && <StructuresLayer/>}
        {(globalState.mode == 'area-builder' || ((globalState.mode == 'areas' || globalState.mode == 'area-editor') && globalState.selectedArea)) && 
          <AreasLayer/>}
        <MarkerLayer layers={globalState.layers}/>

        {/* Circle editor - only show when structures visible, exactly 1 circle selected, not during box-select or areas mode */}
        {globalState.showStructures && globalState.selectedStructures.length === 1 && globalState.selectedStructures[0].type === 'circle' &&
          globalState.mode !== 'box-select' && globalState.mode !== 'areas' && globalState.mode !== 'area-editor' &&
          <CircleEditor
            point={globalState.selectedStructures[0].point}
            radius={globalState.selectedStructures[0].radius}
            onChange={globalState.updateSelectedStructureCircle}
            onCancel={() => globalState.clearStructureSelection()}
            onDelete={globalState.deleteSelectedStructures}
        />}

        {/* Polygon editor - only show when structures visible, exactly 1 polygon selected, not during box-select or areas mode */}
        {globalState.showStructures && globalState.selectedStructures.length === 1 && globalState.selectedStructures[0].type === 'polygon' &&
          globalState.mode !== 'box-select' && globalState.mode !== 'areas' && globalState.mode !== 'area-editor' &&
          <PolygonEditor
            points={globalState.selectedStructures[0].points}
            onChange={globalState.updateSelectedStructurePolygon}
            onCancel={() => globalState.clearStructureSelection()}
            onDelete={globalState.deleteSelectedStructures}
        />}

        {/* Multi-structure overlay - only show when structures visible, more than 1 selected, not during box-select or areas mode */}
        {globalState.showStructures && globalState.selectedStructures.length > 1 &&
          globalState.mode !== 'box-select' && globalState.mode !== 'areas' && globalState.mode !== 'area-editor' &&
          <MultiStructureOverlay/>}                

        {/* Area editor */}
        {globalState.selectedArea != null && (globalState.mode == 'areas' || globalState.mode == 'area-editor') && 
          <RectEditor 
            rect={globalState.selectedArea.rect}
            resolution={globalState.selectedArea.resolution}
            onChange={globalState.updateSelectedArea}
        />}              

        {/* Circle builder */}
        {globalState.mode == 'circle-builder' && 
          <CircleBuilder onCancel={() => globalState.setMode(null)} onComplete={(point, radius) => globalState.addStructureCircle(point, radius)}/>}

        {/* Polygon builder */}
        {globalState.mode == 'polygon-builder' && 
          <PolygonBuilder onCancel={() => globalState.setMode(null)} onComplete={(points) => globalState.addStructurePolygon(points)}/>}          

        {/* Area builder */}
        {globalState.mode == 'area-builder' &&
          <RectBuilder onCancel={() => globalState.setMode(null)} onComplete={(rect) => globalState.addArea(rect)}/>}

        {/* Box select builder */}
        {globalState.mode == 'box-select' &&
          <BoxSelectBuilder
            onCancel={() => globalState.setMode(null)}
            onComplete={(selected) => {
              globalState.setSelectedStructures(selected);
              globalState.setMode(null);
            }}
          />}

        {/* Eyedropper tool */}
        {globalState.mode == 'eyedropper' &&
          <Eyedropper onCancel={() => globalState.setMode(null)} />}

        <Dock top={16} right={64} bottom={64}>
          {globalState.selectedStructures.length > 0 && globalState.mode == null && <Dockable longitude={0} latitude={0} alwaysDocked>
            <StructureInfoBox value={globalState.selectedStructures[0]}/>
          </Dockable>}
          {globalState.selectedArea && globalState.mode == 'area-editor' && <Dockable longitude={0} latitude={0} alwaysDocked>
            <AreaInfoBox value={globalState.selectedArea}/>
          </Dockable>}
        </Dock>

        <Legends/>

        {globalState.organix != null &&
          <OrganixManager
             area={globalState.organix.area}
             intent={globalState.organix.intent}
             onClose={() => globalState.stopOrganix()}/>}

      </LocalMap>
    </div>
  );
}

const MapViewStyled = styled(MapViewBase)`
  position: absolute;
  left: 80px;
  top: 0;
  right: 0;
  bottom: 0;
  background: #333;
`

const MapView = (props: IProps) => <MapViewStyled {...props}/>

export { MapView }


